import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import brandingConfig from '../../config/brandingConfig';
import { useTransportWording } from '../../utils/transportWording';

// Styled components
const PolicyContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '900px',
  margin: '0 auto',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1),
}));

const TableStyled = styled(Table)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& th, & td': {
    borderColor: theme.palette.divider,
  },
}));

const PrivacyPolicy = () => {
  const wording = useTransportWording();
  // Aktuelles Jahr für Copyright
  const currentYear = new Date().getFullYear();
  // Aktuelles Datum für Stand der Datenschutzerklärung
  const currentDate = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Container>
      <PolicyContainer elevation={2}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Datenschutzerklärung für die {brandingConfig.appName} Admin-Plattform
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Stand: {currentDate}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h5" component="h2">
          1. Verantwortlicher im Sinne der DSGVO
        </SectionTitle>
        <Typography variant="body1" paragraph>
          {brandingConfig.owner && <>{brandingConfig.owner}<br /></>}
          {brandingConfig.companyName}<br />
          {brandingConfig.address}<br />
          Telefon: {brandingConfig.phone}<br />
          E-Mail: {brandingConfig.email}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          (Im White-Label-Betrieb kann an dieser Stelle das jeweils lizenznehmende Taxiunternehmen als Verantwortlicher genannt werden.)
        </Typography>

        <SectionTitle variant="h5" component="h2">
          2. (Optionaler) Datenschutzbeauftragter
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Nach derzeitiger Mitarbeiteranzahl besteht keine gesetzliche Verpflichtung zur Bestellung eines Datenschutzbeauftragten (vgl. § 38 BDSG). Bei Datenschutzanfragen wenden Sie sich bitte an die oben genannten Kontaktdaten.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          3. Zwecke & Rechtsgrundlagen der Datenverarbeitung
        </SectionTitle>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <TableStyled size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Zweck</strong></TableCell>
                <TableCell><strong>Betroffene Personen</strong></TableCell>
                <TableCell><strong>Rechtsgrundlage</strong></TableCell>
                <TableCell><strong>Erläuterung</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Verwaltung des {wording.company}s</TableCell>
                <TableCell>Administratoren, Disponenten, Fahrer</TableCell>
                <TableCell>Art. 6 Abs. 1 b DSGVO (Vertragserfüllung)</TableCell>
                <TableCell>Verarbeitung von Benutzer-, Fahrer-, Fahrzeug- und Fahrtdaten zur Bereitstellung der Admin-Plattform</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Fahrtvermittlung & Disposition</TableCell>
                <TableCell>Fahrer, Fahrgäste</TableCell>
                <TableCell>Art. 6 Abs. 1 b DSGVO (Vertragserfüllung)</TableCell>
                <TableCell>Verwaltung von Fahrtbuchungen, Zuweisung von Fahrern, Schichtplanung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Live-Standortansicht</TableCell>
                <TableCell>Fahrer (Fahrzeugposition)</TableCell>
                <TableCell>Art. 6 Abs. 1 a DSGVO (Einwilligung)</TableCell>
                <TableCell>Live-Tracking von Fahrzeugen auf der Karte zur Disposition; Fahrer stimmen über die mobile App zu</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rückwirkende Fahrerstandorte</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Vom Verantwortlichen vor Aktivierung anhand des Beschäftigungs- und Vertragsverhältnisses festzulegen</TableCell>
                <TableCell>Nachvollziehung konkreter Fahrten und Reklamationen durch berechtigte Disponenten; Unternehmerkonten sehen nur eigene Fahrer</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Berichts- & Analysefunktionen</TableCell>
                <TableCell>Administratoren, Disponenten</TableCell>
                <TableCell>Art. 6 Abs. 1 f DSGVO (berechtigtes Interesse)</TableCell>
                <TableCell>Erstellung von Berichten und Statistiken zur Unternehmensoptimierung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Aufrechterhaltung der Plattform</TableCell>
                <TableCell>alle Nutzer</TableCell>
                <TableCell>Art. 6 Abs. 1 f DSGVO (berechtigtes Interesse)</TableCell>
                <TableCell>Betriebssicherheit, Fehlerdiagnose, Missbrauchsprävention</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Gesetzliche Pflichten</TableCell>
                <TableCell>Administratoren, Disponenten, Fahrer</TableCell>
                <TableCell>Art. 6 Abs. 1 c DSGVO (gesetzliche Pflicht)</TableCell>
                <TableCell>Aufbewahrung von Daten gemäß PBefG, AO, HGB und anderen Vorschriften</TableCell>
              </TableRow>
            </TableBody>
          </TableStyled>
        </TableContainer>

        <SectionTitle variant="h5" component="h2">
          4. Datenkategorien
        </SectionTitle>
        <Typography variant="body1" component="ul">
          <li>Administratordaten: UID, Name, Telefonnummer, E-Mail-Adresse, Rolle (Administrator/Disponent), Zugriffsberechtigungen</li>
          <li>Fahrerdaten: Name, Telefonnummer, E-Mail-Adresse, Führerscheininformationen, Beschäftigungsstatus</li>
          <li>Fahrzeugdaten: Fahrzeug-ID, Typ, Kennzeichen, Fahrer-ID, Verfügbarkeitsstatus, Wartungsinformationen</li>
          <li>Fahrtdaten: Abhol- & Zieladressen, Datum/Uhrzeit, Preisschätzung, Fahrtstatus, Bewertungsdaten</li>
          <li>Standortdaten: GPS-Koordinaten von Fahrern (werden aus der mobilen App übertragen)</li>
          <li>Kundendaten: Name, Telefonnummer (bei Bestellungen über die Admin-Plattform)</li>
          <li>Schicht- & Verfügbarkeitsdaten (Dienstpläne)</li>
          <li>Chat- und Benachrichtigungsdaten</li>
          <li>Zahlungsdaten bei App-Zahlungen</li>
          <li>Telefonie- und Anrufdaten einschließlich KI-Gesprächszusammenfassungen, sofern die Telefoniefunktion genutzt wird</li>
          <li>Verordnungs- und Krankenfahrtbelege, sofern diese Funktion genutzt wird</li>
          <li>Konfigurationsdaten (White-Label-Brand-Farben, Feature-Flags)</li>
        </Typography>

        <SectionTitle variant="h5" component="h2">
          5. Empfänger / Weitergabe der Daten
        </SectionTitle>
        <Typography variant="body1" component="ul">
          <li>Fahrer: erhalten nur die für sie relevanten Fahrtinformationen und Schichtpläne</li>
          <li>Firebase / Google Ireland Ltd.: Cloud-Hosting, Datenbank, Authentifizierung, Server-Logfiles (Auftragsverarbeiter, SCC gemäß Art. 46 DSGVO)</li>
          <li>Google Maps Platform: Routenberechnung, Geocoding, Kartendarstellung; Anfragen enthalten Standort- und Adressdaten</li>
          <li>PayPal: Zahlungsabwicklung bei App-Zahlungen</li>
          <li>fonio: Verarbeitung von Telefonanrufen im Auftrag des {wording.company}s, sofern der KI-Telefonist genutzt wird</li>
          <li>Keine Datenweitergabe an Werbenetzwerke oder Social-Media-Dienste</li>
        </Typography>

        <SectionTitle variant="h5" component="h2">
          6. Datenverarbeitung in Drittländern
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Die Verarbeitung erfolgt ausschließlich in Rechenzentren innerhalb des Europäischen Wirtschaftsraums (EWR). Sollte ausnahmsweise ein Zugriff durch Google-Unternehmen in den USA erfolgen, geschieht dies auf Basis der EU-Standardvertragsklauseln (SCC, 2021/914/EU).
        </Typography>

        <SectionTitle variant="h5" component="h2">
          7. Speicher- & Löschfristen
        </SectionTitle>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <TableStyled size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Datenkategorie</strong></TableCell>
                <TableCell><strong>Löschfrist</strong></TableCell>
                <TableCell><strong>Mechanismus</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Fahrtdaten (inkl. temporäre Standortdaten)</TableCell>
                <TableCell>120 Tage nach Abschluss der Fahrt</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Standort-Live-Daten</TableCell>
                <TableCell>werden mit neuer Position überschrieben</TableCell>
                <TableCell>—</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Historische Fahrerstandorte</TableCell>
                <TableCell>60 Tage, optional 90 Tage nach dokumentierter Betreiberentscheidung</TableCell>
                <TableCell>Automatische Firestore-TTL-Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Administrator- & Disponentenprofile</TableCell>
                <TableCell>bis zur Löschung des Accounts</TableCell>
                <TableCell>manuelle Löschfunktion durch Administrator</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Fahrerprofile</TableCell>
                <TableCell>6 Monate nach Beendigung des Arbeitsverhältnisses</TableCell>
                <TableCell>manuelle Archivierungsfunktion</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Fahrzeugdaten</TableCell>
                <TableCell>12 Monate nach Außerbetriebnahme</TableCell>
                <TableCell>manuelle Archivierungsfunktion</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Chat-Nachrichten</TableCell>
                <TableCell>60 Tage</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Benachrichtigungen</TableCell>
                <TableCell>60 Tage</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Zahlungsdaten bei App-Zahlungen</TableCell>
                <TableCell>mindestens 180 Tage</TableCell>
                <TableCell>Automatische Löschung nach Ablauf der Frist; gesetzliche Pflichten bleiben unberührt</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Telefonie- und Anrufdaten einschließlich KI-Gesprächszusammenfassungen</TableCell>
                <TableCell>30 Tage</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Deaktivierte Web-Push-Subscriptions</TableCell>
                <TableCell>90 Tage nach Deaktivierung</TableCell>
                <TableCell>Automatische Löschung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Verordnungs- und Krankenfahrtbelege</TableCell>
                <TableCell>derzeit keine automatische Löschung</TableCell>
                <TableCell>Manuelle Löschung durch den Verantwortlichen unter Beachtung von Abrechnungs- und Aufbewahrungspflichten</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rechnungs- & Steuerdaten</TableCell>
                <TableCell>6 / 10 Jahre (AO/HGB)</TableCell>
                <TableCell>gesetzliche Aufbewahrungspflicht</TableCell>
              </TableRow>
            </TableBody>
          </TableStyled>
        </TableContainer>

        <SectionTitle variant="h5" component="h2">
          8. Ihre Rechte
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Sie haben jederzeit das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung (Art. 18), Datenübertragbarkeit (Art. 20) sowie Widerspruch gegen bestimmte Verarbeitungen (Art. 21).
          Anfragen richten Sie bitte an: {brandingConfig.email}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          9. Widerrufsrecht bei Einwilligungen
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Erteilte Einwilligungen können jederzeit widerrufen werden. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          10. Pflicht zur Bereitstellung
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Die Bereitstellung von personenbezogenen Daten im Rahmen der Admin-Plattform ist notwendig für die Erfüllung des Arbeitsverhältnisses sowie gesetzlicher und vertraglicher Pflichten. Ohne diese Daten können bestimmte Funktionen der Plattform nicht genutzt werden.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          11. Datensicherheit
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Alle Datenübertragungen erfolgen TLS-verschlüsselt. Daten in Firebase werden verschlüsselt im Ruhezustand gespeichert; Zugriffe sind rollenbasiert limitiert. Regelmäßige Back-ups und Pen-Tests gewährleisten den Schutz vor Datenverlust und unbefugtem Zugriff. Eine detaillierte Zugriffssteuerung sorgt dafür, dass Benutzer nur auf die für ihre Rolle relevanten Daten zugreifen können.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          12. Cookies und ähnliche Technologien
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Die Admin-Plattform verwendet technisch notwendige Cookies für die Authentifizierung und Sitzungsverwaltung. Diese sind für den Betrieb der Plattform erforderlich. Firebase Authentication nutzt Session-Cookies zur Aufrechterhaltung der Anmeldesitzung.
        </Typography>
        <Typography variant="body1" paragraph>
          Wir verwenden keine Analyse-Cookies oder Tracking-Technologien von Drittanbietern für Marketingzwecke. Alle Plattform-Nutzungsstatistiken werden anonymisiert erhoben und dienen ausschließlich der Verbesserung der Benutzerfreundlichkeit.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          13. Beschwerderecht bei der Aufsichtsbehörde
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren.
          {brandingConfig.dataProtectionAuthority
            ? ` Zuständig ist: ${brandingConfig.dataProtectionAuthority.name}, ${brandingConfig.dataProtectionAuthority.address}.`
            : ' Zuständig ist z. B. das Unabhängige Landeszentrum für Datenschutz Schleswig-Holstein (ULD), Holstenstraße 98, 24103 Kiel, Deutschland.'}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          14. Änderungen dieser Datenschutzerklärung
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Wir passen diese Datenschutzerklärung an, sobald Änderungen der Datenverarbeitung dies erforderlich machen. Die jeweils aktuelle Version ist stets in der Admin-Plattform abrufbar.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="textSecondary">
            © {currentYear} {brandingConfig.companyName}
          </Typography>
        </Box>
      </PolicyContainer>
    </Container>
  );
};

export default PrivacyPolicy;
